/**
 * SMS -> voice call OTP failover.
 *
 * The backend sends the 2FA code by SMS and returns how it was delivered (`otp` of POST 2fa-otp / login):
 * { channel, primary_channel, fallback_channel, timeout_seconds, auto_failover, failover_available }.
 * When the countdown ends without a valid code, the app asks for the code again with `otp_failover: true`
 * and the backend calls the user to read it out.
 */

export const OTP_CHANNELS = {
  sms: "sms",
  call: "call",
};

export const OTP_FAILOVER_STATUSES = {
  /** Counting down before the automatic voice call. */
  countingDown: "counting_down",
  failingOver: "failing_over",
  /** No further failover: the call was placed or none is available. */
  settled: "settled",
  error: "error",
};

export function createFailoverState(delivery, now) {
  const totalSeconds = Math.max(1, delivery.timeout_seconds);

  return {
    status: delivery.failover_available
      ? OTP_FAILOVER_STATUSES.countingDown
      : OTP_FAILOVER_STATUSES.settled,
    activeChannel: delivery.channel,
    totalSeconds,
    /** Epoch ms when the countdown reaches zero. */
    deadline: now + totalSeconds * 1000,
    /** The code now comes by voice call (automatic or after the countdown). */
    usedFailover: Boolean(delivery.auto_failover),
    /** The backend called right away because the number can't receive SMS. */
    autoFailover: Boolean(delivery.auto_failover),
    error: null,
  };
}

export function getSecondsLeft(state, now) {
  if (state.status !== OTP_FAILOVER_STATUSES.countingDown) {
    return 0;
  }

  return Math.max(0, Math.ceil((state.deadline - now) / 1000));
}

export function failoverStarted(state) {
  return { ...state, status: OTP_FAILOVER_STATUSES.failingOver, error: null };
}

export function failoverSucceeded(state, delivery) {
  return {
    ...state,
    status: OTP_FAILOVER_STATUSES.settled,
    activeChannel: delivery.channel,
    usedFailover: true,
  };
}

export function failoverFailed(state, error) {
  return { ...state, status: OTP_FAILOVER_STATUSES.error, error };
}
